import { apiClient as api } from "./httpClient";

/**
 * Convênios (operadoras de plano de saúde e empresas conveniadas) e planos, por entidade (RN-411).
 * Cada tela usa a permissão do próprio menu (`health-plan-operators`, `health-plans`); a aba
 * Financeiro do paciente recebe as opções pela consulta dela e não chama estas rotas.
 */
export type AgreementKind = "operator" | "company";
export type AgreementPayer = "patient" | "company";

export const AGREEMENT_KIND_LABEL: Record<AgreementKind, string> = {
  operator: "Operadora de plano de saúde",
  company: "Empresa conveniada",
};

export const AGREEMENT_PAYER_LABEL: Record<AgreementPayer, string> = {
  patient: "Paciente, com o preço do convênio",
  company: "Empresa, faturada",
};

export interface HealthPlanOperatorRow {
  id: string;
  kind: AgreementKind;
  tradeName: string;
  ansRegistration: string | null;
  providerCode: string | null;
  document: string | null;
  companyName: string | null;
  paymentDeadlineDays: number | null;
  contactName: string | null;
  phone: string | null;
  email: string | null;
  startDate: string | null;
  endDate: string | null;
  payer: AgreementPayer | null;
  eligibilityProof: string | null;
  notes: string | null;
  active: boolean;
  plansCount: number;
}

export type SaveHealthPlanOperatorPayload = Omit<HealthPlanOperatorRow, "id" | "plansCount"> & { id: string | null };

export interface HealthPlanRow {
  id: string;
  operatorId: string;
  operatorName: string;
  operatorActive: boolean;
  name: string;
  ansProductCode: string | null;
  copayment: boolean | null;
  startDate: string | null;
  endDate: string | null;
  active: boolean;
  patientsCount: number;
  /** Plano ativo que não vale hoje porque o CONVÊNIO está inativo/fora de vigência (RN-411f). */
  blockedByOperator: boolean;
}

export interface HealthPlanOperatorOption {
  id: string;
  name: string;
  kind: AgreementKind;
  active: boolean;
}

export interface SaveHealthPlanPayload {
  id: string | null;
  operatorId: string;
  name: string;
  ansProductCode: string | null;
  copayment: boolean | null;
  startDate: string | null;
  endDate: string | null;
  active: boolean;
}

export const healthPlanOperatorsService = {
  async getAll(): Promise<HealthPlanOperatorRow[]> {
    const response = await api.get("/health-plan-operators");
    return response.data;
  },
  async save(payload: SaveHealthPlanOperatorPayload): Promise<string> {
    const response = await api.post("/health-plan-operators", payload);
    return response.data.id;
  },
  async remove(id: string): Promise<void> {
    await api.delete(`/health-plan-operators/${id}`);
  },
};

export const healthPlansService = {
  async getAll(): Promise<{ plans: HealthPlanRow[]; operators: HealthPlanOperatorOption[] }> {
    const response = await api.get("/health-plans");
    return response.data;
  },
  async save(payload: SaveHealthPlanPayload): Promise<string> {
    const response = await api.post("/health-plans", payload);
    return response.data.id;
  },
  async remove(id: string): Promise<void> {
    await api.delete(`/health-plans/${id}`);
  },
};

/** Vigência para exibição: "desde 01/09/2026", "até 31/12/2026", "01/09/2026 a 31/12/2026" ou "—". */
export const formatValidity = (start: string | null, end: string | null): string => {
  const br = (iso: string) => {
    const [y, m, d] = iso.slice(0, 10).split("-");
    return `${d}/${m}/${y}`;
  };
  if (start && end) return `${br(start)} a ${br(end)}`;
  if (start) return `desde ${br(start)}`;
  if (end) return `até ${br(end)}`;
  return "—";
};
